/**
 * Modale de création d'une non-conformité (QUAL-001 §5.2 / §5.5).
 *
 * Même patron que ClientCreateDialog / LearnerCreateDialog / CreateInstructorDialog
 * (shadcn `Dialog`, footer Annuler/Créer, puis navigation vers la fiche). Réutilisée
 * par le registre (« + NC ») ET le lanceur transverse `ReportNonConformityAction`
 * (pré-remplie source + contexte).
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Building2, Link2, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { QualityReferentialPicker } from '@/components/quality/QualityReferentialPicker'
import { useConstatDUneNCNeuve } from '@/components/quality/useConstatDUneNCNeuve'
import { useEntity } from '@/contexts/EntityContext'
import { fromISODate, toISODate } from '@/lib/date-utils'
import { contextHintsFromSource } from '@/lib/qualitySource'
import { toastApiError } from '@/lib/toast-error'
import { useCreateNonConformity } from '@/hooks/useNonConformities'
import { NC_SEVERITIES } from '@/hooks/useQuality.types'
import type {
    NCContext,
    NCSeverity,
    NCSourceChannel,
    NonConformityDetail,
    SourceObjectType,
} from '@/hooks/useQuality.types'

interface Props {
    entityId: string | null | undefined
    open: boolean
    onOpenChange: (open: boolean) => void
    onCreated?: (nc: NonConformityDetail) => void
    source?: {
        type: SourceObjectType
        id: string
        label: string
        /** Canal d'origine ; `manual` par défaut. Voir `reportNCRowAction`. */
        channel?: NCSourceChannel
    } | null
    contextHints?: NCContext | null
}

export function NonConformityCreateDialog({
    entityId,
    open,
    onOpenChange,
    onCreated,
    source,
    contextHints,
}: Props) {
    const { t } = useTranslation()
    const createMutation = useCreateNonConformity(entityId)
    // D143 — « Constatée le » pré-rempli et borné au jour de l'ORGANISME.
    const constat = useConstatDUneNCNeuve()
    const { jour, detectedAt, repartir } = constat

    // Sur quelle entité la fiche va-t-elle NAÎTRE ? La question ne se pose que si
    // l'utilisateur en a plusieurs — et elle se pose vraiment : le lanceur transverse
    // reste actif sur une ressource HÉRITÉE (une formation du siège vue depuis une
    // agence), et la non-conformité naît alors sur l'entité COURANTE, pas sur celle
    // qui détient l'objet signalé. Sans cette ligne, rien ne le dit (arbitrage PO du
    // 2026-08-13). En mono-entité, l'afficher serait du bruit.
    const { currentEntity, entities } = useEntity()
    const showOwningEntity = entities.length > 1 && Boolean(currentEntity?.entityName)

    const [title, setTitle] = useState('')
    const [severity, setSeverity] = useState<NCSeverity>('minor')
    const [originCode, setOriginCode] = useState<string | null>(null)
    const [vectorCode, setVectorCode] = useState<string | null>(null)
    const [causeCode, setCauseCode] = useState<string | null>(null)
    const [description, setDescription] = useState('')

    useEffect(() => {
        if (open) {
            setTitle('')
            setSeverity('minor')
            repartir()
            setOriginCode(null)
            setVectorCode(null)
            setCauseCode(null)
            setDescription('')
        }
    }, [open, repartir])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!title.trim()) return
        try {
            const nc = await createMutation.mutateAsync({
                title: title.trim(),
                severity,
                detected_at: detectedAt,
                description: description.trim() || null,
                origin_code: originCode,
                vector_code: vectorCode,
                cause_code: causeCode,
                // ⚠️ `manual` était EN DUR : toute non-conformité ouverte depuis une
                // réponse de satisfaction était enregistrée comme saisie à la main.
                // Le registre expose pourtant une colonne « Canal » filtrable, et
                // c'est par elle qu'un auditeur vérifie l'indicateur Qualiopi 32 —
                // « mesures d'amélioration issues de l'ANALYSE des appréciations ».
                // Filtré sur « Satisfaction », le registre renvoyait zéro.
                source_channel: source?.channel ?? 'manual',
                source_object_type: source?.type ?? null,
                source_object_id: source?.id ?? null,
                source_object_label: source?.label ?? null,
                // Auto-remplit le contexte (parties prenantes) depuis la fiche
                // d'origine : le slot correspondant (client/apprenant/…) est
                // pré-rempli pour toute fiche, sans que chacune ait à le passer.
                context: contextHints ?? contextHintsFromSource(source),
            })
            toast.success(t('quality.nc.createSuccess', 'Non-conformité créée'))
            onOpenChange(false)
            onCreated?.(nc)
        } catch (err) {
            // D143 — un refus de DATE se dit sous « Constatée le », dans la
            // langue du lecteur, avec la borne acceptée : la saisie reste là.
            if (constat.direLeRefus(err)) return
            toastApiError(err, t('quality.nc.createError', 'Impossible de créer la non-conformité'))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t('quality.nc.createTitle', 'Nouvelle non-conformité')}</DialogTitle>
                    <DialogDescription>
                        {t('quality.nc.createSubtitle', 'Enregistrez un écart constaté à traiter.')}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 py-2">
                        {source && (
                            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                                <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <span className="truncate">{source.label}</span>
                            </div>
                        )}

                        {showOwningEntity && (
                            <div className="flex items-center gap-2 px-1 text-sm text-muted-foreground">
                                <Building2 className="h-4 w-4 shrink-0" />
                                <span className="truncate">
                                    {t('quality.nc.createdOnEntity', {
                                        entity: currentEntity?.entityName,
                                        defaultValue: 'Enregistrée sur : {{entity}}',
                                    })}
                                </span>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label htmlFor="nc-title">{t('quality.nc.fields.title', 'Nom')} *</Label>
                            <Input
                                id="nc-title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={255}
                                autoFocus
                                className="text-base md:text-sm"
                            />
                        </div>

                        {/* Un champ par ligne sous md (règle projet, DS §18.2). Même
                            correctif que la fiche action
                            (`pages/quality/components/ActionDetailFields.tsx`) : à 390 px
                            de viewport la modale n'offre que 310 px utiles, que
                            `grid-cols-2` nue découpait en colonnes de 149 px — 77 px de
                            texte au DatePicker « Constatée le » pour une date qui en
                            mesure 71 à 75. La rangée `md:grid-cols-3` juste en dessous
                            était, elle, déjà mobile-first : c'est la seule ici à ne pas
                            l'être. */}
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label>{t('quality.nc.fields.severity', 'Gravité')}</Label>
                                <Select value={severity} onValueChange={(v) => setSeverity(v as NCSeverity)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {NC_SEVERITIES.map((s) => (
                                            <SelectItem key={s} value={s}>
                                                {t(`quality.nc.severity.${s}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="nc-detected-at">{t('quality.nc.fields.detectedAt', 'Constatée le')}</Label>
                                <DatePicker
                                    id="nc-detected-at"
                                    value={detectedAt ? fromISODate(detectedAt) : null}
                                    onChange={(d) => constat.choisir(d ? toISODate(d) : '')}
                                    maxDate={jour.borneHaute}
                                    today={jour.aujourdhuiDate}
                                    aria-describedby={constat.refus ? 'nc-detected-at-refus' : undefined}
                                />
                                {constat.refus && (
                                    <p id="nc-detected-at-refus" role="alert" className="text-xs text-destructive">
                                        {constat.refus}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-3">
                            <div className="space-y-1.5">
                                <Label>{t('quality.nc.fields.origin', 'Origine')}</Label>
                                <QualityReferentialPicker
                                    entityId={entityId}
                                    refType="NC_ORIGIN"
                                    value={originCode}
                                    onChange={setOriginCode}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t('quality.nc.fields.vector', 'Vecteur')}</Label>
                                <QualityReferentialPicker
                                    entityId={entityId}
                                    refType="NC_VECTOR"
                                    value={vectorCode}
                                    onChange={setVectorCode}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t('quality.nc.fields.cause', 'Cause principale')}</Label>
                                <QualityReferentialPicker
                                    entityId={entityId}
                                    refType="NC_CAUSE"
                                    value={causeCode}
                                    onChange={setCauseCode}
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="nc-desc">{t('quality.nc.fields.description', 'Description')}</Label>
                            <Textarea
                                id="nc-desc"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                className="text-base md:text-sm"
                            />
                        </div>
                    </div>

                    <DialogFooter className="mt-4">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            {t('common.cancel', 'Annuler')}
                        </Button>
                        <Button type="submit" disabled={!title.trim() || createMutation.isPending}>
                            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('common.create', 'Créer')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
